import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Grid,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  Download,
  FileText,
  Image as ImageIcon,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Play,
  Search,
  Trash2,
  Upload,
  Folder as FolderIcon,
  FolderInput,
} from 'lucide-react';
import { useAtom, useAtomValue } from 'jotai';
import { documentsByFamilyAtom } from '../store';
import {
  useAuth,
  useFamily,
  useHeaderActions,
  useNavAction,
  useOnKeysArrived,
  useLoadingState,
} from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import {
  documentSecret,
  fetchAllPages,
  isE2EEReady,
  isLockedApp,
  matchesQuery,
  updateSealedDocument,
  type DocumentEnvelope,
} from '../lib/e2ee/apps';
import {
  dropHeldDocuments,
  heldDocumentsAtom,
  retryHeldDocuments,
  uploadDocumentFiles,
} from '../lib/documentUploads';
import { fetchDecryptedBlob, useMediaUrl } from '../lib/e2ee/media';
import { saveBlob } from '../lib/download';
import { api, ApiError, type FamilyDocument } from '../lib/api';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { ConfirmSheet } from '../components/ConfirmSheet';
import {
  DocumentDetails,
  type DocumentChanges,
} from '../components/documents/DocumentDetails';
import { EmptyState } from '../components/EmptyState';
import { appLocale } from '../lib/format';
import { useLanguage, t as translateNow, useT, type MessageKey } from '../i18n';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

type Translate = ReturnType<typeof useT>;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

type Filter = 'all' | 'photos' | 'pdfs' | 'videos' | 'other';
const FILTERS: Array<{ key: Filter; label: MessageKey }> = [
  { key: 'all', label: 'apps.documents.filter.all' },
  { key: 'photos', label: 'apps.documents.filter.photos' },
  { key: 'pdfs', label: 'apps.documents.filter.pdfs' },
  { key: 'videos', label: 'apps.documents.filter.videos' },
  { key: 'other', label: 'apps.documents.filter.other' },
];

function kindOf(document: FamilyDocument): Exclude<Filter, 'all'> {
  if (document.mimeType.startsWith('image/')) return 'photos';
  if (document.mimeType === 'application/pdf') return 'pdfs';
  if (document.mimeType.startsWith('video/')) return 'videos';
  return 'other';
}

// "PDF", "DOC", "TXT" for the tile.
function extensionLabel(document: FamilyDocument, t: Translate): string {
  const ext = document.fileName.includes('.')
    ? document.fileName.split('.').pop()!.toUpperCase()
    : '';
  if (ext === 'DOCX') return 'DOC';
  return ext.slice(0, 4) || t('apps.documents.fileTile');
}

// "today", "yesterday", "Mon", "12 Oct".
function when(iso: string, t: Translate, now = new Date()): string {
  const date = new Date(iso);
  const days = Math.floor(
    (new Date(now.toDateString()).getTime() -
      new Date(date.toDateString()).getTime()) /
      86_400_000
  );
  if (days <= 0) return t('apps.documents.today');
  if (days === 1) return t('apps.documents.yesterday');
  if (days < 7)
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

function Thumbnail({
  document,
  height,
}: {
  document: FamilyDocument;
  height: string;
}) {
  const t = useT();
  const kind = kindOf(document);
  // Encrypted photos are decrypted on the device (M28, D65).
  const secret = documentSecret(document);
  const photo = useMediaUrl(
    kind === 'photos' ? document.url : undefined,
    secret.file,
    secret.mimeType
  );
  if (kind === 'photos') {
    return (
      <Image
        src={photo.src}
        alt=""
        h={height}
        w="full"
        objectFit="cover"
        bg="gray.200"
        loading="lazy"
        fallback={
          <Box
            h={height}
            bg="gray.200"
            color="muted"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ImageIcon size={28} aria-hidden="true" />
          </Box>
        }
      />
    );
  }
  if (kind === 'videos') {
    return (
      <Box
        h={height}
        bg="#2F2C29"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box
          w="40px"
          h="40px"
          borderRadius="full"
          bg="rgba(250, 249, 247, 0.92)"
          color="#1F1D1B"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Play size={18} fill="currentColor" aria-hidden="true" />
        </Box>
      </Box>
    );
  }
  const pdf = kind === 'pdfs';
  return (
    <Box
      h={height}
      bg={pdf ? 'accent.tint' : 'surface.subtle'}
      color={pdf ? 'accent' : 'ink.body'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <FileText size={28} aria-hidden="true" />
      <Text fontSize="12px" fontWeight={700} letterSpacing="0.06em">
        {extensionLabel(document, t)}
      </Text>
    </Box>
  );
}

/** A document's details as an envelope, to seal again with changes (D65). */
function documentEnvelope(document: FamilyDocument): DocumentEnvelope {
  const opened = document.e2eeApp?.envelope as DocumentEnvelope | undefined;
  if (opened) return opened;
  return {
    fileName: document.fileName,
    mimeType: document.mimeType,
    fileSize: document.fileSize,
    description: document.description,
    folder: document.folder ?? null,
    tags: document.tags ?? [],
    plainFile: true,
  };
}

// Documents (Living Room): filter chips, then the family's files as cards.
export function DocumentsPage() {
  const { currentFamilyId, currentRole, families } = useFamily();
  const { currentUser } = useAuth();
  const toast = useToast();
  const t = useT();
  const language = useLanguage();
  const [documentsMap, setDocumentsMap] = useAtom(documentsByFamilyAtom);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<FamilyDocument | null>(null);
  // Files kept while this device waits for its key in an encrypted family:
  // nothing was uploaded; they stay with their own family, and Retry (or its
  // keys arriving) sends them there (M28).
  const heldByFamily = useAtomValue(heldDocumentsAtom);
  const heldFiles = currentFamilyId
    ? (heldByFamily[currentFamilyId] ?? [])
    : [];
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Folders and tags (M26, D49).
  const [folder, setFolder] = useState<string | null>(null);
  const [folders, setFolders] = useState<
    Array<{ folder: string; count: number }>
  >([]);
  const [detailsId, setDetailsId] = useState<string | null>(null);
  const [savingDetails, setSavingDetails] = useState(false);

  const familyName =
    families.find((f) => f.id === currentFamilyId)?.name ??
    t('apps.shared.theFamily');
  const allDocuments = currentFamilyId
    ? (documentsMap[currentFamilyId] ?? [])
    : [];
  // Encrypted documents (M28, D65) are searched and filed here: the server
  // can't read their names, notes, folders or tags.
  const sealedMode = isE2EEReady();
  const documents = sealedMode
    ? allDocuments.filter(
        (document) =>
          (!folder || document.folder === folder) &&
          (!query.trim() ||
            matchesQuery(query, [
              document.fileName,
              document.description,
              document.folder,
              ...(document.tags ?? []),
            ]))
      )
    : allDocuments;
  const shown = documents.filter(
    (document) => filter === 'all' || kindOf(document) === filter
  );
  const localFolders = [
    ...allDocuments
      .reduce((counts, document) => {
        if (document.folder) {
          counts.set(document.folder, (counts.get(document.folder) ?? 0) + 1);
        }
        return counts;
      }, new Map<string, number>())
      .entries(),
  ]
    .map(([name, count]) => ({ folder: name, count }))
    .sort((a, b) => a.folder.localeCompare(b.folder));
  const folderList = sealedMode ? localFolders : folders;
  const canModerate = currentRole === 'root' || currentRole === 'admin';

  // The nav's "+" uploads here.
  // On an empty page the upload button is in the content instead.
  useNavAction(
    uploading || documents.length === 0 ? null : t('apps.documents.upload'),
    () => fileInputRef.current?.click()
  );

  const setDocuments = useCallback(
    (
      familyId: string,
      updater: (current: FamilyDocument[]) => FamilyDocument[]
    ) => {
      setDocumentsMap((current) => ({
        ...current,
        [familyId]: updater(current[familyId] ?? []),
      }));
    },
    [setDocumentsMap]
  );

  const load = useCallback(
    async (familyId: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = isE2EEReady()
          ? await fetchAllPages((cursor) =>
              api.getFamilyDocuments(familyId, { cursor })
            )
          : (
              await api.getFamilyDocuments(familyId, {
                q: query.trim() || undefined,
                folder: folder ?? undefined,
              })
            ).data;
        setDocumentsMap((current) => ({
          ...current,
          [familyId]: data,
        }));
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : translateNow('apps.documents.loadFailed')
        );
      } finally {
        setLoading(false);
      }
    },
    [query, folder, setDocumentsMap]
  );

  const loadFolders = useCallback((familyId: string) => {
    api
      .getDocumentFolders(familyId)
      .then((response) => setFolders(response.data))
      .catch(() => undefined);
  }, []);
  useEffect(() => {
    setFolder(null);
    if (currentFamilyId) loadFolders(currentFamilyId);
  }, [currentFamilyId, loadFolders]);

  const saveDetails = async (changes: DocumentChanges) => {
    if (!currentFamilyId || !detailsId) return;
    setSavingDetails(true);
    try {
      const current = allDocuments.find(
        (document) => document.id === detailsId
      );
      // Never write over what this device can't open (#82).
      if (current && isLockedApp(current)) return;
      const response =
        isE2EEReady() && current
          ? await updateSealedDocument(currentFamilyId, detailsId, {
              ...documentEnvelope(current),
              description: changes.description,
              folder: changes.folder,
              tags: changes.tags,
            })
          : await api.updateFamilyDocument(currentFamilyId, detailsId, changes);
      setDocuments(currentFamilyId, (current) =>
        current.map((document) =>
          document.id === detailsId ? response.data : document
        )
      );
      loadFolders(currentFamilyId);
      setDetailsId(null);
    } catch (err) {
      toast({
        title: t('apps.documents.saveFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setSavingDetails(false);
    }
  };

  // With encryption everything is loaded once and filtered here.
  const serverQuery = sealedMode ? '' : query;
  const serverFolder = sealedMode ? null : folder;
  // The mark while the documents load; the family's cached ones show at once (M66, D248).
  const documentsState = useLoadingState({
    loading,
    hasData: allDocuments.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    if (!currentFamilyId) return;
    const timer = setTimeout(
      () => void load(currentFamilyId),
      serverQuery ? 300 : 0
    );
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFamilyId, serverQuery, serverFolder]);
  useOnKeysArrived('family', currentFamilyId, () => {
    if (currentFamilyId) void load(currentFamilyId);
  });

  // Encrypted files open (or download) after they're decrypted here.
  const openDocument = async (document: FamilyDocument, save = false) => {
    const secret = documentSecret(document);
    if (!secret.file) {
      window.open(document.url, '_blank', 'noreferrer');
      return;
    }
    const tab = save ? null : window.open('', '_blank');
    try {
      const blob = await fetchDecryptedBlob(
        document.url,
        secret.file,
        secret.mimeType
      );
      if (save || !tab) {
        saveBlob(blob, document.fileName);
      } else {
        tab.location.href = URL.createObjectURL(blob);
      }
    } catch {
      tab?.close();
      toast({ title: t('apps.documents.openFailed'), status: 'error' });
    }
  };
  const openHandler =
    (document: FamilyDocument, save = false) =>
    (event: React.MouseEvent) => {
      if (!documentSecret(document).file) return;
      event.preventDefault();
      void openDocument(document, save);
    };

  const addDocument = (familyId: string, document: FamilyDocument) =>
    setDocuments(familyId, (current) => [
      document,
      ...current.filter((item) => item.id !== document.id),
    ]);

  const sendFiles = async (
    familyId: string,
    upload: () => Promise<'done' | 'held' | 'none'>
  ) => {
    setUploading(true);
    try {
      if ((await upload()) === 'done') {
        toast({ title: t('apps.documents.added'), status: 'success' });
      }
    } catch (err) {
      toast({
        title: t('apps.documents.uploadFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleFiles = (files: FileList) => {
    const familyId = currentFamilyId;
    if (!familyId) return;
    void sendFiles(familyId, () =>
      uploadDocumentFiles(familyId, Array.from(files), addDocument)
    );
  };

  const retryHeld = (familyId: string) =>
    void sendFiles(familyId, () => retryHeldDocuments(familyId, addDocument));

  useOnKeysArrived('family', currentFamilyId, () => {
    if (currentFamilyId && heldFiles.length && !uploading) {
      retryHeld(currentFamilyId);
    }
  });

  const confirmDelete = async () => {
    const document = deleting;
    if (!currentFamilyId || !document) return;
    try {
      await api.deleteFamilyDocument(currentFamilyId, document.id);
      setDocuments(currentFamilyId, (current) =>
        current.filter((item) => item.id !== document.id)
      );
      toast({ title: t('apps.documents.deleted'), status: 'success' });
    } catch (err) {
      toast({
        title: t('apps.documents.deleteFailed'),
        description:
          err instanceof ApiError ? err.message : t('common.somethingWrong'),
        status: 'error',
      });
    } finally {
      setDeleting(null);
    }
  };

  // Search and grid/list sit beside the title (design).
  useHeaderActions(
    <>
      <IconButton
        aria-label={
          searchOpen ? t('apps.shared.closeSearch') : t('apps.documents.search')
        }
        aria-pressed={searchOpen}
        icon={<Search size={22} />}
        variant="ghost"
        colorScheme="gray"
        size="lg"
        onClick={() => {
          setSearchOpen((open) => !open);
          if (searchOpen) setQuery('');
        }}
      />
      <IconButton
        aria-label={
          view === 'grid'
            ? t('apps.documents.listView')
            : t('apps.documents.gridView')
        }
        icon={
          view === 'grid' ? <ListIcon size={22} /> : <LayoutGrid size={22} />
        }
        variant="ghost"
        colorScheme="gray"
        size="lg"
        onClick={() =>
          setView((current) => (current === 'grid' ? 'list' : 'grid'))
        }
      />
    </>,
    [searchOpen, view, language]
  );

  if (!currentFamilyId) return <NoFamilySelected />;

  const who = (document: FamilyDocument) =>
    document.uploadedBy === currentUser?.id
      ? t('common.you')
      : (document.uploadedByUser?.displayName.split(' ')[0] ??
        t('apps.shared.family'));

  // Tags as small chips; tapping one searches for it (D50).
  const tagsRow = (document: FamilyDocument) =>
    (document.tags ?? []).length > 0 ? (
      <HStack spacing={1} px={3} pb={3} mt="-6px" flexWrap="wrap" pr={10}>
        {(document.tags ?? []).map((tag) => (
          <Box
            key={tag}
            as="button"
            type="button"
            fontSize="12px"
            fontWeight={600}
            color="accent"
            bg="accent.tint"
            borderRadius="full"
            px={2}
            py="1px"
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              setSearchOpen(true);
              setQuery(tag);
            }}
          >
            #{tag}
          </Box>
        ))}
      </HStack>
    ) : null;

  const actions = (document: FamilyDocument) => (
    <Menu placement="bottom-end" isLazy>
      <MenuButton
        as={IconButton}
        aria-label={t('apps.shared.moreFor', { name: document.fileName })}
        icon={<MoreHorizontal size={18} />}
        size="sm"
        variant="ghost"
        colorScheme="gray"
      />
      <MenuList>
        <MenuItem
          as="a"
          href={document.url}
          target="_blank"
          rel="noreferrer"
          download={document.fileName}
          onClick={openHandler(document, true)}
          icon={<Download size={16} />}
        >
          {t('apps.documents.download')}
        </MenuItem>
        {(document.uploadedBy === currentUser?.id || canModerate) && (
          <>
            {/* A locked one can't be resealed with new details (#82). */}
            {!isLockedApp(document) && (
              <MenuItem
                icon={<FolderInput size={16} />}
                onClick={() => setDetailsId(document.id)}
              >
                {t('apps.documents.folderTags')}
              </MenuItem>
            )}
            <MenuItem
              icon={<Trash2 size={16} />}
              color="danger"
              onClick={() => setDeleting(document)}
            >
              {t('apps.documents.deleteMenu')}
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );

  return (
    <Box h="full" overflowY="auto" sx={{ overscrollBehaviorY: 'contain' }}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        multiple
        accept=".pdf,.doc,.docx,.txt,image/*,video/*,audio/*"
        onChange={(event) => {
          if (event.target.files?.length) handleFiles(event.target.files);
          event.target.value = '';
        }}
      />

      <Box maxW="1100px" mx="auto" px={4} pb={6}>
        {/* Filters (hidden when there is nothing to filter) */}
        <HStack
          spacing={2}
          pt="2px"
          pb="14px"
          display={
            documents.length === 0 && !loading && !query ? 'none' : 'flex'
          }
        >
          <HStack
            spacing={2}
            flex="1"
            minW={0}
            overflowX="auto"
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
            role="group"
            aria-label={t('apps.documents.filterLabel')}
          >
            {FILTERS.map(({ key, label }) => {
              const on = filter === key;
              return (
                <Button
                  key={key}
                  aria-pressed={on}
                  onClick={() => setFilter(key)}
                  h="40px"
                  px={4}
                  flexShrink={0}
                  fontWeight={600}
                  fontSize="15px"
                  bg={on ? 'ink' : 'surface'}
                  color={on ? 'canvas' : 'ink'}
                  borderWidth={on ? 0 : '1px'}
                  borderColor="line"
                  _hover={{ opacity: 0.9 }}
                >
                  {t(label)}
                </Button>
              );
            })}
          </HStack>
        </HStack>

        {folderList.length > 0 && (
          <HStack
            spacing={2}
            pb="14px"
            mt="-4px"
            overflowX="auto"
            sx={{
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
            role="group"
            aria-label={t('apps.documents.folders')}
          >
            {[
              { folder: null, count: 0 } as {
                folder: string | null;
                count: number;
              },
              ...folderList,
            ].map((entry) => {
              const on = folder === entry.folder;
              return (
                <Button
                  key={entry.folder ?? 'all'}
                  aria-pressed={on}
                  onClick={() => setFolder(entry.folder)}
                  h="36px"
                  px={3}
                  flexShrink={0}
                  fontWeight={600}
                  fontSize="14px"
                  leftIcon={<FolderIcon size={15} />}
                  colorScheme="gray"
                  bg={on ? 'accent.tint' : 'transparent'}
                  color={on ? 'accent' : 'muted'}
                  _hover={{ bg: 'surface.subtle' }}
                >
                  {entry.folder === null
                    ? t('apps.documents.allFolders')
                    : `${entry.folder} · ${entry.count}`}
                </Button>
              );
            })}
          </HStack>
        )}

        {searchOpen && (
          <InputGroup mb={3}>
            <InputLeftElement pointerEvents="none" color="muted">
              <Search size={18} />
            </InputLeftElement>
            <Input
              autoFocus
              placeholder={t('apps.documents.search')}
              aria-label={t('apps.documents.search')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              borderRadius="full"
            />
          </InputGroup>
        )}

        {error && !documentsState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {heldFiles.length > 0 && (
          <Alert status="info" borderRadius="xl" mb={3} alignItems="center">
            <AlertIcon />
            <Text flex="1" minW={0}>
              {t('e2ee.keysNotReady')}
            </Text>
            <HStack spacing={1} flexShrink={0}>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => dropHeldDocuments(currentFamilyId)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                isLoading={uploading}
                onClick={() => retryHeld(currentFamilyId)}
              >
                {t('common.retry')}
              </Button>
            </HStack>
          </Alert>
        )}

        {documentsState.show ? (
          <LoadingPanel
            state={documentsState}
            label={t('apps.documents.loading')}
          />
        ) : shown.length === 0 ? (
          <EmptyState
            kind="documents"
            title={
              filter === 'all' && !query
                ? t('apps.documents.emptyTitle')
                : t('apps.shared.nothingHere')
            }
            body={
              filter === 'all' && !query
                ? t('apps.documents.emptyBody', { family: familyName })
                : t('apps.documents.noMatch')
            }
            action={
              filter === 'all' && !query
                ? {
                    label: t('apps.documents.upload'),
                    icon: <Upload size={20} />,
                    onClick: () => fileInputRef.current?.click(),
                  }
                : undefined
            }
          />
        ) : view === 'grid' ? (
          <Grid
            templateColumns={{
              base: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(4, minmax(0, 1fr))',
            }}
            gap="10px"
          >
            {shown.map((document) => (
              <Box
                key={document.id}
                bg="surface"
                borderWidth="1px"
                borderColor="line"
                borderRadius="20px"
                overflow="hidden"
                position="relative"
              >
                <Box
                  as="a"
                  href={document.url}
                  onClick={openHandler(document)}
                  target="_blank"
                  rel="noreferrer"
                  display="block"
                  aria-label={t('apps.documents.open', {
                    name: document.fileName,
                  })}
                >
                  <Thumbnail document={document} height="108px" />
                  <VStack align="stretch" spacing="2px" px={3} pt="10px" pb={3}>
                    <Text fontWeight={600} fontSize="15px" noOfLines={1} pr={6}>
                      {document.fileName}
                    </Text>
                    {isUnbound(document) && <UnboundMark record />}
                    <Text fontSize="13px" color="muted" noOfLines={1}>
                      {document.folder ? `${document.folder} · ` : ''}
                      {who(document)} · {when(document.createdAt, t)}
                    </Text>
                  </VStack>
                </Box>
                {tagsRow(document)}
                <Box position="absolute" right={1} bottom={1}>
                  {actions(document)}
                </Box>
              </Box>
            ))}
          </Grid>
        ) : (
          <VStack align="stretch" spacing={2}>
            {shown.map((document) => (
              <HStack
                key={document.id}
                bg="surface"
                borderWidth="1px"
                borderColor="line"
                borderRadius="18px"
                p="10px"
                spacing={3}
              >
                <Box
                  w="52px"
                  h="52px"
                  borderRadius="14px"
                  overflow="hidden"
                  flexShrink={0}
                >
                  <Thumbnail document={document} height="52px" />
                </Box>
                <Box
                  as="a"
                  href={document.url}
                  onClick={openHandler(document)}
                  target="_blank"
                  rel="noreferrer"
                  flex="1"
                  minW={0}
                  aria-label={t('apps.documents.open', {
                    name: document.fileName,
                  })}
                >
                  <Text fontWeight={700} fontSize="16px" noOfLines={1}>
                    {document.fileName}
                  </Text>
                  {isUnbound(document) && <UnboundMark record />}
                  <Text fontSize="14px" color="muted" noOfLines={1}>
                    {document.folder ? `${document.folder} · ` : ''}
                    {who(document)} · {when(document.createdAt, t)} ·{' '}
                    {formatBytes(document.fileSize)}
                  </Text>
                  {tagsRow(document)}
                </Box>
                {actions(document)}
              </HStack>
            ))}
          </VStack>
        )}
      </Box>

      <DocumentDetails
        document={
          documents.find(
            (document) => document.id === detailsId && !isLockedApp(document)
          ) ?? null
        }
        folders={folderList.map((entry) => entry.folder)}
        isSaving={savingDetails}
        onSave={(changes) => void saveDetails(changes)}
        onClose={() => setDetailsId(null)}
      />

      {/* Delete a file (design: bottom sheet) */}
      <ConfirmSheet
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title={t('apps.documents.deleteTitle')}
        body={t('apps.documents.deleteBody', { family: familyName })}
        preview={
          deleting && (
            <HStack spacing={3} p="10px" borderRadius="18px" bg="canvas">
              <Box
                w="52px"
                h="52px"
                borderRadius="14px"
                overflow="hidden"
                flexShrink={0}
              >
                <Thumbnail document={deleting} height="52px" />
              </Box>
              <Box minW={0}>
                <Text fontWeight={700} fontSize="16px" noOfLines={1}>
                  {deleting.fileName}
                </Text>
                <Text fontSize="14px" color="muted">
                  {t('apps.documents.addedBy', { name: who(deleting) })} ·{' '}
                  {formatBytes(deleting.fileSize)}
                </Text>
              </Box>
            </HStack>
          )
        }
        confirmLabel={t('apps.documents.deleteConfirm')}
        confirmIcon={<Trash2 size={18} />}
        cancelLabel={t('apps.shared.keepIt')}
        onConfirm={() => void confirmDelete()}
      />
    </Box>
  );
}
